import { memo, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Warning as WarningIcon } from '@mui/icons-material';

interface UnsubscribeConfirmProps {
  service: string;
  accountId: string;
  region: string;
  confirmToken: string;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const UnsubscribeConfirm = memo<UnsubscribeConfirmProps>(({
  service,
  accountId,
  region,
  confirmToken,
  submitting,
  onCancel,
  onConfirm,
}) => {
  const [typed, setTyped] = useState('');
  const matched = typed.trim() === confirmToken;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'warning.light',
        bgcolor: (theme) => alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
        borderRadius: 1,
        p: 1.5,
        mt: 1.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'warning.main', fontSize: 13, fontWeight: 500, mb: 0.75 }}>
        <WarningIcon sx={{ fontSize: 14 }} /> Unsubscribe from service
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.5 }}>
        Removes the subscription to{' '}
        <Box component="strong" sx={{ fontFamily: 'monospace' }}>{service}</Box> in account{' '}
        <strong>{accountId}</strong> ({region}). The reconciler tears down every deployment in this
        service. Type <Box component="strong" sx={{ fontFamily: 'monospace' }}>{confirmToken}</Box>{' '}
        to enable.
      </Typography>
      <TextField
        size="small"
        fullWidth
        placeholder={confirmToken}
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        disabled={submitting}
        slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.25 }}>
        <Button size="small" onClick={onCancel} disabled={submitting}>Cancel</Button>
        <Button
          size="small"
          variant="outlined"
          color="warning"
          disabled={!matched || submitting}
          onClick={onConfirm}
        >
          Unsubscribe
        </Button>
      </Box>
    </Box>
  );
});

UnsubscribeConfirm.displayName = 'UnsubscribeConfirm';
export default UnsubscribeConfirm;
